import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { formatStorage, isLiveSubscription, type BillingCheckout, type BillingUrl } from "@omnabase/shared";
import { api, fetchWorkspace } from "./api";
import { useSetParam } from "./nav";
import { useSession } from "../shell/store";

/** The plan through Stripe (M7 round four): the owner's ways onto Stripe's hosted pages. Checkout buys a plan for a workspace
 *  without a live subscription, the Portal changes everything once there is one (the card, the plan, the cancellation); round five's
 *  `pack` opens a one-off Checkout for a credit pack (every plan may buy one). Each asks the API for the page's address and sends the
 *  browser there; `busy` names the button that is on its way (the plan's or pack's id, or "portal") so it can show it is working, and
 *  an error lands in a toast. */
export function useStripePages(): { busy: string | null; checkout: (planId: BillingCheckout["planId"]) => void; pack: (packId: string) => void; portal: () => void } {
  const { workspace, toast } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const go = async (key: string, path: "checkout" | "portal", body?: unknown) => {
    setBusy(key);
    try { window.location.assign((await api.post<BillingUrl>(`/api/v1/w/${workspace.slug}/billing/${path}`, body)).url); }
    catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(null); }
  };
  return { busy, checkout: (planId) => void go(planId, "checkout", { planId }), pack: (packId) => void go(packId, "checkout", { packId }), portal: () => void go("portal", "portal") };
}

/** Back from Stripe's Checkout (`?checkout=done` for a plan, round five's `?pack=done` for a credit pack): reload the workspace, say
 *  so once, and drop the parameter. Stripe's notice usually arrives before the browser does; when it has not yet, the toast says the
 *  plan follows (a pack's credits show in the balance, which the reload refreshes). */
export function useCheckoutReturn(): void {
  const { workspace, reload, toast } = useSession();
  const [params] = useSearchParams();
  const setParam = useSetParam();
  const back = params.get("checkout") === "done" ? "checkout" : params.get("pack") === "done" ? "pack" : null;
  const handled = useRef(false);
  useEffect(() => {
    if (!back || handled.current) return;
    handled.current = true;
    setParam(back, null);
    void (async () => {
      const w = back === "checkout" ? await fetchWorkspace(workspace.slug).catch(() => null) : null;
      await reload();
      if (back === "pack") toast("Payment received. The credits show in the balance as soon as Stripe confirms it.", { kind: "success" });
      else toast(w && isLiveSubscription(w.plan.subscription.status) ? `Welcome to ${w.plan.name}` : "Payment received. The plan switches as soon as Stripe confirms it.");
    })();
  }, [back, workspace.slug, reload, toast, setParam]);
}

/** The Billing card's storage line (the files round, 2026-10-30; brief rule 5, finding 34): "Storage: 0.8 of 1 GB used", always; the warning
 *  tone past 80 percent, the error tone at or over the cap. Under 0.1 GB the used part reads in MB ("Storage: 12 MB of 1 GB used"). */
export type StorageTone = "normal" | "warning" | "error";
export function storageState(used: number, cap: number): { line: string; tone: StorageTone } {
  const mb = used / 1024 ** 2;
  const usedPart = used < 0.1 * 1024 ** 3 ? `${String(mb < 9.95 ? Math.round(mb * 10) / 10 : Math.round(mb))} MB` : formatStorage(used).replace(/ GB$/, "");
  const tone: StorageTone = cap > 0 && used >= cap ? "error" : cap > 0 && used > cap * 0.8 ? "warning" : "normal";
  return { line: `Storage: ${usedPart} of ${formatStorage(cap)} used`, tone };
}
